import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import api from "../lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from Google OAuth, let AuthCallback establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.twofa_required) return data;
    setUser(data.user);
    queryClient.invalidateQueries({ queryKey: ["public-settings"] });
    return data.user;
  };
  const verify2fa = async (challenge, code) => {
    const { data } = await api.post("/auth/2fa/verify", { challenge, code });
    setUser(data.user);
    queryClient.invalidateQueries({ queryKey: ["public-settings"] });
    return data.user;
  };
  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    setUser(data.user);
    return data.user;
  };
  const logout = async () => {
    await api.post("/auth/logout");
    setUser(null);
    queryClient.invalidateQueries({ queryKey: ["public-settings"] });
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, verify2fa, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}
